import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Text from "../../../components/atoms/Text";
import Button from "../../../components/atoms/Button";
import { DateRangePicker } from "../../../components/molecules/DateRangePicker";
import { getDateStringFromToday } from "../../../utils/TimeUtil";
import { AdminAIReviewTable } from "../../../components/templates/AdminAIReviewDetailPopupTemplate/AdminAIReviewTable";
import { fetchAdminAIReviewsList, updateAdminAIReviewStatus } from "../../../api/aiReviewService";
import Switch from "../../../components/atoms/Switch";
import { refundAIReviewPoints } from "../../../api/pointsService";
import { ToastManager } from "../../../components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "../../../components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ConfirmManager } from "../../../components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { AIReview, AIReviewStatus } from "common/models";
import DownloadUtil from "../../../utils/DownloadUtil";
import { exportAIReviewsToCSV, getFormattedDate } from "../../../utils/csvExportUtil";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  width: 100%;
`;

const CompareButton = styled(Button)`
  margin-left: 12px;
  min-width: 120px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled(Text)`
  min-width: 80px;
  font-weight: 500;
  color: #666;
`;

const DateFilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
`;

const QuickDateButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#ddd")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#666")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const CSVButton = styled(Button)`
  margin-left: auto;
  min-width: 140px;
`;

const AdminAIReviewsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<any[]>([]);
  const [selectedReviewIds, setSelectedReviewIds] = useState<Set<number>>(new Set());

  // 필터 상태
  const [startDate, setStartDate] = useState(getDateStringFromToday({ days: -7 })); // 기본 1주일
  const [endDate, setEndDate] = useState(getDateStringFromToday());
  const [quickDateRange, setQuickDateRange] = useState("1week");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // 날짜 빠른 선택
  const handleQuickDateSelect = (range: string) => {
    setQuickDateRange(range);
    const today = getDateStringFromToday();

    switch (range) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "1week":
        setStartDate(getDateStringFromToday({ days: -7 }));
        setEndDate(today);
        break;
      case "1month":
        setStartDate(getDateStringFromToday({ days: -30 }));
        setEndDate(today);
        break;
      case "3months":
        setStartDate(getDateStringFromToday({ days: -90 }));
        setEndDate(today);
        break;
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (name: "start" | "end", date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    if (name === "start") {
      setStartDate(dateString);
    } else {
      setEndDate(dateString);
    }
    setQuickDateRange(""); // 직접 선택 시 빠른 선택 해제
  };

  // 데이터 로드
  const loadReviews = async () => {
    try {
      setLoading(true);
      WaitingManager.show({ message: "AI 리뷰 목록을 불러오는 중..." });

      // endDate에 23:59:59 추가하여 오늘 데이터 포함
      const adjustedEndDate = endDate + " 23:59:59";

      const response = await fetchAdminAIReviewsList({
        startDate,
        endDate: adjustedEndDate,
        start: 0,
        count: 1000, // 전체 데이터 로드 후 프론트엔드 필터링
        withEvaluationId: true, // 최신 SUCCESS 평가 ID 포함
        includeDeleted, // 삭제된 항목 포함 여부
      });

      setReviews(response.data);
      setTotal(response.data.length);
    } catch (error) {
      console.error("Failed to load AI reviews:", error);
      ToastManager.show({ content: "AI 리뷰 목록을 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
      WaitingManager.hide();
    }
  };

  // 검색 필터링
  const filterReviews = () => {
    if (!searchKeyword.trim()) {
      setFilteredReviews(reviews);
      return;
    }

    const keyword = searchKeyword.toLowerCase();
    const filtered = reviews.filter((review) => {
      // role 검색 ("경기도"로 검색 시 경기도약사회 권한 검색)
      const hasMatchingRole = () => {
        if (!review.user?.roles) return false;
        
        if (keyword === "경기도" || keyword === "경기") {
          return review.user.roles.includes("경기도약사회");
        }
        if (keyword === "서울시" || keyword === "서울") {
          return review.user.roles.some(role => role.startsWith("서울시약사회"));
        }
        if (keyword === "인천시" || keyword === "인천") {
          return review.user.roles.some(role => role.startsWith("인천시약사회"));
        }
        if (keyword === "admin" || keyword === "adm" || keyword === "관리자" || keyword === "관리") {
          return review.user.roles.includes("admin");
        }
        
        // 일반적인 role 검색
        return review.user.roles.some(role => role.toLowerCase().includes(keyword));
      };
      
      return (
        review.id?.toString().includes(keyword) ||
        review.user?.email?.toLowerCase().includes(keyword) ||
        review.user?.name?.toLowerCase().includes(keyword) ||
        review.user?.phone?.includes(searchKeyword) || // 전화번호는 원본 그대로 검색
        hasMatchingRole()
      );
    });

    setFilteredReviews(filtered);
  };

  // 페이지별 데이터
  const paginatedReviews = filteredReviews.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // 환불 처리
  const handleRefund = async (review: AIReview) => {
    const cost = review.analysis?.cost || 0;
    const userEmail = review.user?.email || "사용자";

    return new Promise<void>((resolve) => {
      ConfirmManager.show({
        title: "포인트 환불",
        body: `${userEmail}님의 AI 리뷰 #${review.id}에 사용된 포인트를 환불하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            size="Medium"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={() => {
              ConfirmManager.hide();
              resolve();
            }}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            size="Medium"
            type="Text"
            variant="Contained"
            color="Primary"
            onClick={async () => {
              ConfirmManager.hide();
              try {
                WaitingManager.show({ message: "포인트 환불 처리 중..." });

                const result = await refundAIReviewPoints({
                  reviewId: review.id,
                  userId: review.userId,
                });

                if (result.error === 0) {
                  ToastManager.show({ content: "포인트 환불이 완료되었습니다." });
                  loadReviews(); // 목록 새로고침
                } else {
                  ToastManager.show({ content: result.message || "포인트 환불에 실패했습니다." });
                }
              } catch (error) {
                console.error("Refund failed:", error);
                ToastManager.show({ content: "포인트 환불 처리에 실패했습니다." });
              } finally {
                WaitingManager.hide();
              }
              resolve();
            }}
          >
            환불
          </Button>,
        ],
      });
    });
  };

  // 상세보기 팝업
  const handleViewDetail = (reviewId: string) => {
    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
    const width = Math.min(1920, window.screen.width * 0.9);
    const height = Math.min(1080, window.screen.height * 0.9);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/admin/ai-reviews/${reviewId}/popup`,
      `ai-review-${reviewId}`,
      `width=${width},height=${height},left=${left},top=${top},` +
        `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
        `scrollbars=yes,resizable=yes`
    );
  };

  // 강제 실패 처리 (더블클릭)
  const handleForceFailReview = async (review: AIReview) => {
    // 완료/실패 상태면 무시
    if (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED) {
      return;
    }

    return new Promise<void>((resolve) => {
      ConfirmManager.show({
        title: "실패 처리",
        body: `AI 리뷰 #${review.id}를 실패 처리하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            size="Medium"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={() => {
              ConfirmManager.hide();
              resolve();
            }}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            size="Medium"
            type="Text"
            variant="Contained"
            color="Negative"
            onClick={async () => {
              ConfirmManager.hide();
              try {
                WaitingManager.show({ message: "실패 처리 중..." });

                const result = await updateAdminAIReviewStatus(review.id, AIReviewStatus.FAILED);

                if (!result.error) {
                  ToastManager.show({ content: "실패 처리가 완료되었습니다." });
                  loadReviews(); // 목록 새로고침
                } else {
                  ToastManager.show({ content: result.message || "실패 처리에 실패했습니다." });
                }
              } catch (error) {
                console.error("Force fail failed:", error);
                ToastManager.show({ content: "실패 처리에 실패했습니다." });
              } finally {
                WaitingManager.hide();
              }
              resolve();
            }}
          >
            실패 처리
          </Button>,
        ],
      });
    });
  };

  // 체크박스 선택 핸들러
  const handleSelectReview = (reviewId: number, checked: boolean) => {
    const newSelection = new Set(selectedReviewIds);
    
    if (checked) {
      if (newSelection.size >= 2) {
        ToastManager.show({ content: "최대 2개까지만 선택할 수 있습니다." });
        return;
      }
      newSelection.add(reviewId);
    } else {
      newSelection.delete(reviewId);
    }
    
    setSelectedReviewIds(newSelection);
  };

  // 비교 버튼 핸들러
  const handleCompare = () => {
    const [leftId, rightId] = Array.from(selectedReviewIds);
    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
    const width = Math.min(1920, window.screen.width * 0.9);
    const height = Math.min(1080, window.screen.height * 0.9);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      `/admin/ai-reviews/compare/popup?left=${leftId}&right=${rightId}`,
      `ai-review-compare-${leftId}-${rightId}`,
      `width=${width},height=${height},left=${left},top=${top},` +
      `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
      `scrollbars=yes,resizable=yes`
    );
  };

  // CSV 다운로드 핸들러
  const handleDownloadCSV = () => {
    try {
      if (filteredReviews.length === 0) {
        ToastManager.show({ content: "다운로드할 데이터가 없습니다." });
        return;
      }

      // CSV 데이터 생성
      const csvData = exportAIReviewsToCSV(filteredReviews);
      
      // 파일명 생성
      const filename = `AI_리뷰_보고서_${getFormattedDate()}.csv`;
      
      // 다운로드 실행
      DownloadUtil.downloadCsv(csvData, filename);
      
      ToastManager.show({ 
        content: `${filteredReviews.length}건의 데이터를 다운로드했습니다.` 
      });
    } catch (error) {
      console.error("CSV 다운로드 실패:", error);
      ToastManager.show({ content: "CSV 다운로드에 실패했습니다." });
    }
  };

  // 초기 로드 및 날짜 변경, 삭제포함 옵션 변경 시 재로드
  useEffect(() => {
    loadReviews();
  }, [startDate, endDate, includeDeleted]);

  // 검색어 변경 시 필터링
  useEffect(() => {
    filterReviews();
    setCurrentPage(1); // 검색 시 첫 페이지로
    setSelectedReviewIds(new Set()); // 검색 시 선택 초기화
  }, [searchKeyword, reviews]);

  return (
    <Container>
      <Header>
        <Title>AI 리뷰 보고서 관리</Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterLabel>기간 선택</FilterLabel>
          <DateFilterWrapper>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChangeDate={handleDateChange}
              disabled={loading}
            />
            <QuickDateButton
              active={quickDateRange === "today"}
              onClick={() => handleQuickDateSelect("today")}
            >
              오늘
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1week"}
              onClick={() => handleQuickDateSelect("1week")}
            >
              1주일
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1month"}
              onClick={() => handleQuickDateSelect("1month")}
            >
              1개월
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "3months"}
              onClick={() => handleQuickDateSelect("3months")}
            >
              3개월
            </QuickDateButton>
            {selectedReviewIds.size === 2 && (
              <CompareButton
                size="Medium"
                type="Text"
                variant="Contained"
                color="Primary"
                onClick={handleCompare}
              >
                선택한 리뷰 비교
              </CompareButton>
            )}
            <CSVButton
              size="Medium"
              type="Text"
              variant="Contained"
              color="Primary"
              onClick={handleDownloadCSV}
              disabled={loading || filteredReviews.length === 0}
            >
              CSV 다운로드 ({filteredReviews.length}건)
            </CSVButton>
          </DateFilterWrapper>
        </FilterRow>

        <FilterRow>
          <FilterLabel>검색</FilterLabel>
          <SearchInput
            type="text"
            placeholder="사용자 이름, 이메일, 전화번호, 보고서 번호, 권한(경기도, 서울시, 인천시, admin)으로 검색..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </FilterRow>

        <FilterRow>
          <FilterLabel>옵션</FilterLabel>
          <Switch
            size="Medium"
            isActive={includeDeleted}
            label="삭제된 항목 포함"
            labelPosition="Right"
            onClick={() => setIncludeDeleted(!includeDeleted)}
          />
        </FilterRow>
      </FilterSection>

      <AdminAIReviewTable
        reviews={paginatedReviews}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={filteredReviews.length}
        onPageChange={setCurrentPage}
        onRefund={handleRefund}
        onViewDetail={handleViewDetail}
        selectedIds={selectedReviewIds}
        onSelectReview={handleSelectReview}
        showCheckbox={true}
        onRowDoubleClick={handleForceFailReview}
      />
    </Container>
  );
};

export default AdminAIReviewsPage;
